import { get, post } from "@rails/request.js";
import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="snapshot-drawing"
// Manages drawing lines and polygons on a camera snapshot in a Rails app.
// Supports drawing, saving, and loading coordinates with proper scaling.
export default class extends Controller {
  static values = { cameraId: String, imageUrl: String, mode: String };
  static targets = ["canvas", "modeButton", "modal"];

  // Initialize the controller: set up canvas, state, event listeners, and UI.
  connect() {
    this.ctx = this.canvasTarget.getContext("2d");
    this.resetState();
    this.loadSnapshot();
    this.canvasTarget.addEventListener(
      "mousedown",
      this.handleMouseDown.bind(this)
    );
    this.canvasTarget.addEventListener(
      "mouseup",
      this.handleMouseUp.bind(this)
    );
    this.canvasTarget.addEventListener("click", this.handleClick.bind(this));
    this.canvasTarget.addEventListener(
      "dblclick",
      this.handleDoubleClick.bind(this)
    );
    this.updateModeUI();
  }

  // Reset all state variables to their initial values.
  resetState() {
    this.isDrawing = false; // Tracks if a line is being drawn
    this.currentLine = []; // Current line points being drawn
    this.lines = []; // Saved lines
    this.polygons = []; // Saved polygons
    this.currentPolygon = []; // Current polygon points being drawn
    this.areaCount = 0; // Counter for naming polygons (area1, area2, ...)
    this.modeValue = "line"; // Default mode: "line" or "polygon"
    this.snapshotImage = null; // Snapshot image for canvas
    this.imageWidth = null; // Original image width
    this.imageHeight = null; // Original image height
    this.scaleFactor = 1; // Scaling factor for canvas display
    this.lastClickTime = 0; // Timestamp of last click for double-click detection
  }

  // Update mode button styles to reflect the active mode.
  updateModeUI() {
    this.modeButtonTargets.forEach((btn) => {
      const isActive = btn.dataset.mode === this.modeValue;

      // Toggle background color
      btn.classList.toggle("bg-[#0445d8]", isActive);
      btn.classList.toggle("bg-gray-200", !isActive);

      const line = btn.querySelector("line");
      const polygon = btn.querySelector("polygon");

      if (line) {
        line.setAttribute("stroke", isActive ? "white" : "black");
      }

      if (polygon) {
        polygon.setAttribute("stroke", isActive ? "white" : "black");
        polygon.setAttribute("fill", isActive ? "white" : "black");
      }
    });
  }

  // Switch drawing mode (line/polygon) and update UI.
  setMode({
    currentTarget: {
      dataset: { mode },
    },
  }) {
    this.modeValue = mode;
    this.updateModeUI();
  }

  // Load video snapshot, scale canvas, and draw initial image.
  async loadSnapshot() {
    if (this.snapshotImage) return this.drawSnapshot();

    const video = Object.assign(document.createElement("video"), {
      crossOrigin: "anonymous",
      src: this.imageUrlValue,
      autoplay: true,
      muted: true,
      playsInline: true,
    });

    video.addEventListener("loadeddata", () => (video.currentTime = 0));
    video.addEventListener("seeked", () => {
      this.imageWidth = video.videoWidth;
      this.imageHeight = video.videoHeight;
      this.scaleFactor = 1152 / this.imageWidth; // Scale to fit modal (max-w-6xl = 1152px)

      this.canvasTarget.width = this.imageWidth;
      this.canvasTarget.height = this.imageHeight;
      this.canvasTarget.style.width = `${this.imageWidth * this.scaleFactor}px`;
      this.canvasTarget.style.height = `${
        this.imageHeight * this.scaleFactor
      }px`;

      // Capture video frame into a temporary canvas
      const tempCanvas = document.createElement("canvas");
      tempCanvas.width = this.imageWidth;
      tempCanvas.height = this.imageHeight;
      tempCanvas.getContext("2d").drawImage(video, 0, 0);

      this.snapshotImage = new Image();
      this.snapshotImage.onload = () => {
        this.drawSnapshot();
        this.loadSavedCoordinates();
      };
      this.snapshotImage.src = tempCanvas.toDataURL();

      video.remove();
      tempCanvas.remove();
    });
    video.addEventListener("error", () =>
      this.showAlert("Error loading camera stream. Please try again.", false)
    );
  }

  // Load saved coordinates from the backend and render them.
  async loadSavedCoordinates() {
    try {
      const response = await get(
        `/cameras/${this.cameraIdValue}/saved_coordinates`,
        {
          responseKind: "json",
        }
      );
      if (!response.ok) throw new Error("Failed to load saved coordinates");

      const {
        camera: { coordinates },
      } = await response.json;
      if (!coordinates) return;

      this.lines =
        coordinates.lines?.map((line) => [
          { x: line.x1, y: line.y1 },
          { x: line.x2, y: line.y2 },
        ]) || [];

      this.polygons = [];
      this.areaCount = 0;
      Object.entries(coordinates).forEach(([key, value]) => {
        if (key.startsWith("area") && Array.isArray(value)) {
          this.polygons.push(value);
          this.areaCount += 1;
        }
      });

      this.drawSnapshot();
    } catch (error) {
      console.error("Error loading saved coordinates:", error);
    }
  }

  // Convert mouse coordinates to original image scale.
  getMousePosition(e) {
    const rect = this.canvasTarget.getBoundingClientRect();
    return {
      x: Math.round((e.clientX - rect.left) / this.scaleFactor),
      y: Math.round((e.clientY - rect.top) / this.scaleFactor),
    };
  }

  // Start drawing a line on mousedown in line mode.
  handleMouseDown(e) {
    if (this.modeValue !== "line") return;
    const { x, y } = this.getMousePosition(e);
    this.isDrawing = true;
    this.currentLine = [{ x, y }];
  }

  // Finish drawing a line on mouseup in line mode.
  handleMouseUp(e) {
    if (!this.isDrawing || this.modeValue !== "line") return;
    const { x, y } = this.getMousePosition(e);
    this.currentLine.push({ x, y });
    this.lines.push([...this.currentLine]);
    this.drawLine(this.currentLine);
    this.currentLine = [];
    this.isDrawing = false;
  }

  // Add a point to the current polygon on single click in polygon mode.
  handleClick(e) {
    if (this.modeValue !== "polygon") return;
    const currentTime = Date.now();
    if (currentTime - this.lastClickTime < 300) return; // Skip if part of a double-click
    this.lastClickTime = currentTime;

    const { x, y } = this.getMousePosition(e);
    this.currentPolygon.push({ x, y });
    this.drawSnapshot();
    this.drawPolygon(this.currentPolygon);
  }

  // Finish the current polygon on double-click in polygon mode (min 3 points).
  handleDoubleClick() {
    if (this.modeValue !== "polygon" || this.currentPolygon.length < 3) return;
    this.lastClickTime = Date.now();
    this.polygons.push([...this.currentPolygon]);
    this.areaCount += 1;
    this.currentPolygon = [];
    this.drawSnapshot();
  }

  // Redraw the snapshot and all saved shapes on the canvas.
  drawSnapshot() {
    if (!this.snapshotImage) return;
    this.ctx.clearRect(0, 0, this.canvasTarget.width, this.canvasTarget.height);
    this.ctx.drawImage(
      this.snapshotImage,
      0,
      0,
      this.imageWidth,
      this.imageHeight
    );
    this.redrawShapes();
  }

  // Redraw all saved lines and polygons on the canvas.
  redrawShapes() {
    this.lines.forEach((line) => this.drawLine(line));
    this.polygons.forEach((polygon) => this.drawPolygon(polygon));
  }

  // Draw a single red line with specified thickness.
  drawLine([start, end]) {
    if (!start || !end) return;
    this.drawShape({
      style: { strokeStyle: "red", lineWidth: 6 },
      draw: () => {
        this.ctx.moveTo(start.x, start.y);
        this.ctx.lineTo(end.x, end.y);
      },
    });
  }

  // Draw a single blue polygon with semi-transparent fill.
  drawPolygon(points) {
    if (points.length < 2) return;
    this.drawShape({
      style: {
        strokeStyle: "blue",
        lineWidth: 4,
        fillStyle: "rgba(0, 0, 255, 0.2)",
      },
      draw: () => {
        this.ctx.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++)
          this.ctx.lineTo(points[i].x, points[i].y);
        this.ctx.closePath();
      },
      fill: true,
    });
  }

  // Helper to draw shapes with common canvas operations.
  drawShape({ style, draw, fill = false }) {
    Object.assign(this.ctx, style);
    this.ctx.beginPath();
    draw();
    if (fill) this.ctx.fill();
    this.ctx.stroke();
  }

  // Save drawn shapes to the backend as JSON.
  async save(event) {
    event.preventDefault();
    if (this.lines.length === 0 && this.polygons.length === 0)
      return this.showAlert("Please draw at least one line or polygon.", false);

    const coordinates = this.buildCoordinatesPayload();
    const response = await post(
      `/cameras/${this.cameraIdValue}/save_coordinates`,
      {
        body: JSON.stringify({ camera: { coordinates } }),
        contentType: "application/json",
      }
    );

    this.showAlert(
      response.ok ? "Coordinates saved!" : "Error saving coordinates.",
      response.ok
    );
  }

  // Build the coordinates payload for saving to the backend.
  buildCoordinatesPayload() {
    const polygonData = {};
    this.polygons.forEach((points, index) => {
      polygonData[`area${index + 1}`] = points.map(({ x, y }) => ({ x, y }));
    });

    return {
      lines: this.lines.map(([start, end]) => ({
        x1: Math.round(start.x),
        y1: Math.round(start.y),
        x2: Math.round(end.x),
        y2: Math.round(end.y),
      })),
      ...polygonData,
    };
  }

  // Clear all shapes on the canvas (local JavaScript state only).
  clearShapes() {
    this.lines = [];
    this.polygons = [];
    this.currentLine = [];
    this.currentPolygon = [];
    this.drawSnapshot();
  }

  // Clear all shapes on the canvas and reset backend data.
  async clearAndReset() {
    this.clearShapes(); // Reuse clearShapes for local state clearing
    this.areaCount = 0;

    const response = await post(
      `/cameras/${this.cameraIdValue}/save_coordinates`,
      {
        body: JSON.stringify({ camera: { coordinates: {} } }),
        contentType: "application/json",
      }
    );
    this.showAlert(
      response.ok ? "Coordinates cleared!" : "Failed to clear coordinates.",
      response.ok
    );
  }

  // Show success or error alerts consistently.
  showAlert(message, isSuccess) {
    alert(message);
    if (!isSuccess) console.error(message);
  }

  // Close the modal by removing it from the DOM.
  close() {
    this.element.remove();
  }

  // Closes the modal if user clicks outside the modal content area
  handleOutsideClick(event) {
    if (!this.modalTarget.contains(event.target)) {
      this.close()
    }
  }
}
